import { Link } from "react-router-dom";
import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  FeatureCard,
  CTASection,
} from "../../components/marketing-components.js";

export function StatementGeneratorPage() {
  return (
    <PublicLayout>
      <HeroSection
        title="Accessibility statement generator"
        subtitle="Auto-generate compliance statements backed by real scan findings"
      />

      <section className="marketing-section">
        <div className="section-content">
          <h2>What it does</h2>

          <p>
            Your accessibility statement should be honest, detailed, and
            credible. We generate statements based on actual scan findings, not
            boilerplate.
          </p>

          <div className="features-grid features-grid--three">
            <FeatureCard
              title="Auto-generated"
              description="Built from your WCAG scan results, not a template. Specific to your site."
            />
            <FeatureCard
              title="Issue-aware"
              description="Lists actual findings, effort required, and your remediation timeline."
            />
            <FeatureCard
              title="Legally grounded"
              description="Honest about what's tested, what's not, and where manual review matters."
            />
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Example statement sections</h2>

          <div className="statement-example">
            <h3>Compliance commitment</h3>
            <p>
              "We're committed to ensuring digital accessibility. We regularly
              test our website using automated tools and manual review against
              WCAG 2.1 Level AA standards."
            </p>
          </div>

          <div className="statement-example">
            <h3>Testing methodology</h3>
            <p>
              "We test representative pages monthly using Axe, Pa11y, and custom
              checks. We also conduct manual testing with assistive technologies
              and real users."
            </p>
          </div>

          <div className="statement-example">
            <h3>Known issues and timeline</h3>
            <p>
              "As of [date], we have [X] issues being actively remediated. We
              expect to resolve [issues list] by [dates]. Complex features
              listed need additional manual review and will be completed by
              [date]."
            </p>
          </div>

          <div className="statement-example">
            <h3>Feedback and assistance</h3>
            <p>
              "If you encounter accessibility barriers, please contact [your
              contact]. We'll work to find a solution quickly."
            </p>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>Why our statements are different</h2>

          <div className="comparison">
            <div className="comparison-column">
              <h4>❌ Generic statements</h4>
              <ul>
                <li>"We are committed to accessibility"</li>
                <li>No specific standards mentioned</li>
                <li>No testing methodology</li>
                <li>No known issues listed</li>
                <li>Credibility: low</li>
              </ul>
            </div>

            <div className="comparison-column">
              <h4>✓ Our statements</h4>
              <ul>
                <li>"We test monthly against WCAG 2.1 AA"</li>
                <li>Specific testing methods listed</li>
                <li>Real issues + timelines</li>
                <li>Clear contact info</li>
                <li>Credibility: high</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Monthly updates</h2>

          <p>
            Your statement updates automatically after each monthly scan. New
            issues are added, resolved issues are removed, and timelines adjust
            based on your progress.
          </p>

          <p style={{ marginTop: "16px" }}>
            You always have the option to customize the statement text, but the
            findings section stays truthful and automated.
          </p>
        </div>
      </section>

      <CTASection
        title="Generate your first statement"
        description="Start monitoring and we'll generate your accessibility statement automatically."
        primaryCTA={{ label: "Start trial", href: "/sign-in" }}
      />
    </PublicLayout>
  );
}
